import React, { useContext, useState } from "react";

type SearchContextType = {
  destination: string;
  checkIn: Date;
  checkOut: Date;
  adultCount: number;
  childCount: number;
  childAges: number[]; 
  roomCount: number;
  nights: number;
  vacationType: string;
  hotelId: string;
  regionId: string;
  residency: string;
  hotelName: string;
  saveSearchValues: (
    destination: string,
    checkIn: Date,
    checkOut: Date,
    adultCount: number,
    childCount: number,
    childAges: number[],
    roomCount: number,
    nights: number,
    vacationType: string,
    hotelId?: string,
    regionId?: string,
    residency?: string,
    hotelName?: string
  ) => void;
};

const SearchContext = React.createContext<SearchContextType | undefined>(undefined);

type SearchContextProviderProps = {
  children: React.ReactNode;
};

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const SearchContextProvider = ({ children }: SearchContextProviderProps) => {
  const currentDate = new Date();
  const defaultCheckInDate = addDays(currentDate, 5);
  const defaultCheckOutDate = addDays(defaultCheckInDate, 3);

  const [destination, setDestination] = useState<string>(
    () => sessionStorage.getItem("destination") || ""
  );
  const [checkIn, setCheckIn] = useState<Date>(
    () => new Date(sessionStorage.getItem("checkIn") || defaultCheckInDate.toISOString())
  );
  const [checkOut, setCheckOut] = useState<Date>(
    () => new Date(sessionStorage.getItem("checkOut") || defaultCheckOutDate.toISOString())
  );
  const [adultCount, setAdultCount] = useState<number>(() =>
    parseInt(sessionStorage.getItem("adultCount") || "2")
  );
  const [childCount, setChildCount] = useState<number>(() =>
    parseInt(sessionStorage.getItem("childCount") || "0")
  );
  const [childAges, setChildAges] = useState<number[]>(() => {
    const storedChildAges = sessionStorage.getItem("childAges");
    return storedChildAges ? JSON.parse(storedChildAges) : [];
  });
  const [roomCount, setRoomCount] = useState<number>(() => {
    const storedRoomCount = sessionStorage.getItem("roomCount");
    return storedRoomCount ? parseInt(storedRoomCount, 10) : 1;
  });
  const [nights, setNights] = useState<number>(() => {
    const storedNightCount = sessionStorage.getItem("nights");
    return storedNightCount ? parseInt(storedNightCount, 10) : 3;
  });
  const [vacationType, setVacationType] = useState<string>(
    () => sessionStorage.getItem("vacationType") || "STD"
  );
  const [hotelId, setHotelId] = useState<string>(
    () => sessionStorage.getItem("hotelID") || ""
  );
  const [regionId, setRegionId] = useState<string>(
    () => sessionStorage.getItem("regionId") || ""
  );
  const [residency, setResidency] = useState<string>(
    () => sessionStorage.getItem("residency") || "gb"
  );
  const [hotelName, setHotelName] = useState<string>(
    () => sessionStorage.getItem("hotelName") || ""
  );

  const saveSearchValues = (
    destination: string,
    checkIn: Date,
    checkOut: Date,
    adultCount: number,
    childCount: number,
    childAges: number[],
    roomCount: number,
    nights: number,
    vacationType: string,
    hotelId?: string,
    regionId?: string,
    residency?: string,
    hotelName?: string
  ) => {

    // console.log(vacationType, nights, checkIn);
    
    if (checkIn > checkOut) {
      console.error("Check-in date cannot be later than check-out date.");
      return;
    }

    setDestination(destination);
    setCheckIn(checkIn);
    setCheckOut(checkOut);
    setAdultCount(adultCount);
    setChildCount(childCount);
    setChildAges(childAges);
    setRoomCount(roomCount);
    setNights(nights);
    setVacationType(vacationType);
    if (hotelId) {
      setHotelId(hotelId);
    }
    if (regionId) {
      setRegionId(regionId);
    }
    if (residency) {
      setResidency(residency);
    }
    if (hotelName) {
      setHotelName(hotelName);
    }

    sessionStorage.setItem("destination", destination);
    sessionStorage.setItem("checkIn", checkIn.toISOString());
    sessionStorage.setItem("checkOut", checkOut.toISOString());
    sessionStorage.setItem("adultCount", adultCount.toString());
    sessionStorage.setItem("childCount", childCount.toString());
    sessionStorage.setItem("childAges", JSON.stringify(childAges));
    sessionStorage.setItem("roomCount", roomCount.toString());
    sessionStorage.setItem("nights", nights.toString());
    sessionStorage.setItem("vacationType", vacationType);
    if (hotelId) {
      sessionStorage.setItem("hotelId", hotelId);
    }
    if (regionId) {
      sessionStorage.setItem("regionId", regionId);
    }
    if (residency) {
      sessionStorage.setItem("residency", residency);
    }
    if (hotelName) {
      sessionStorage.setItem("hotelName", hotelName);
    }
  };

  return (
    <SearchContext.Provider
      value={{
        destination,
        checkIn,
        checkOut,
        adultCount,
        childCount,
        childAges,
        roomCount,
        nights,
        vacationType,
        hotelId,
        regionId,
        residency,
        hotelName,
        saveSearchValues,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearchContext must be used within a SearchContextProvider");
  }
  return context;
};